/**
 * What a User Component Version's `Specification` stores beyond the canonical MJ `ComponentSpec`, and what readers see
 * (Data Expert v2, plan §3.2, §5 C19 / C20).
 *
 * The Data Strategist's declarations are stored with the version so a modification can rebuild queries on the same
 * rows. Readers of `Specification` (the registry, the chat client, caches) get the client projection: the stored JSON
 * without the Skip-only declarations, except each query's `grain`. Skip code that needs the declarations reads
 * `StoredSpecification`. Pure: no entity, no database.
 */

/** `dataRequirements` fields stored but never sent to a client. */
export const STORED_ONLY_DATA_REQUIREMENTS_FIELDS: readonly string[] = ['fixedDates', 'baseGroups', 'interactions', 'unavailable'];

/** Query requirement fields stored but never sent to a client. */
export const STORED_ONLY_QUERY_FIELDS: readonly string[] = ['baseGroup', 'extraFilters', 'rows', 'topN', 'measures', 'generate', 'notGenerated', 'separateBase', 'view', 'viewException'];

/** Query requirement fields beyond the canonical MJ ones that are stored and sent to clients (#622). */
export const CLIENT_QUERY_EXTRA_FIELDS: readonly string[] = ['grain'];

type JSONObject = { [key: string]: unknown };

/**
 * The stored specification as clients see it: the stored-only declarations removed, here and in every embedded
 * dependency. Text that is not a JSON object, or that carries none of them, is returned unchanged.
 */
export function ProjectStoredSpecificationForClient(stored: string): string {
    if (!stored || !mentionsStoredOnlyField(stored)) return stored;
    let spec: unknown;
    try {
        spec = JSON.parse(stored);
    } catch {
        return stored;
    }
    if (!isObject(spec)) return stored;
    return removeStoredOnlyFields(spec) ? JSON.stringify(spec) : stored;
}

/** Cheap pre-check, so the common case (a spec without declarations) is never parsed. */
function mentionsStoredOnlyField(stored: string): boolean {
    return [...STORED_ONLY_DATA_REQUIREMENTS_FIELDS, ...STORED_ONLY_QUERY_FIELDS].some(f => stored.includes(`"${f}"`));
}

/** Removes the stored-only fields in place; true when anything was removed. */
function removeStoredOnlyFields(spec: JSONObject): boolean {
    let removed = false;
    const requirements = spec['dataRequirements'];
    if (isObject(requirements)) {
        removed = deleteKeys(requirements, STORED_ONLY_DATA_REQUIREMENTS_FIELDS) || removed;
        const queries = requirements['queries'];
        if (Array.isArray(queries)) {
            for (const query of queries) {
                if (isObject(query)) removed = deleteKeys(query, STORED_ONLY_QUERY_FIELDS) || removed;
            }
        }
    }
    const dependencies = spec['dependencies'];
    if (Array.isArray(dependencies)) {
        for (const dependency of dependencies) {
            if (isObject(dependency)) removed = removeStoredOnlyFields(dependency) || removed;
        }
    }
    return removed;
}

function deleteKeys(target: JSONObject, keys: readonly string[]): boolean {
    let removed = false;
    for (const key of keys) {
        if (key in target) {
            delete target[key];
            removed = true;
        }
    }
    return removed;
}

function isObject(value: unknown): value is JSONObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
